import {action, computed, observable, ObservableMap, reaction, runInAction} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {
    IHistoryConsoleSearchParams,
    IHistoryConsoleService,
    IHistoryConsoleSessionResult
} from '@techsee/techsee-client-infra/lib/services/HistoryConsoleService';
import {IUrlParams, IRedirectionService} from '../../services/RedirectionService';
import {AppState} from '../../app.state';
import {getRootStore} from '../../app.bootstrap';
import type {DataTablePaginationState, DataTableSortState} from '../../components/ts-data-table';
import {DEFAULT_PAGE_SIZE} from '../../components/ts-data-table';
import {IReactionDisposer} from 'mobx/lib/core/reaction';
import {ITimelineItem} from './components/timeline/contract';
import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {WifiScanService} from '../../components/connectivity-guru/wifi-analyzer-scan/WifiScanService';
import get from 'lodash/get';
import tsDate from '@techsee/techsee-date';
import {
    ConnectivityGuruThreshold,
    SpotLightLabels,
    ThresholdConfig
} from '../../components/connectivity-guru/connectivity-guru.contracts';
import {ILocalizationService} from '../../services/LocalizationService';
import {ScanDetail, ScanDetailInfoItem} from './components/right-bar/contract';
import {ChannelNumber, ConnectedDevices, Download, SignalStrength, Upload} from './icons';
import {getAccessChannel, getScanImageUrl, isArScan} from './utils';
import {IWifiAnalyzerService as IWifiAnalyzerApiService} from '@techsee/techsee-client-infra/lib/services/WifiAnalyzerService';
import {LOG_EVENTS, EVENT_LEVELS, EVENT_SOURCES} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {CompareController, ICompareController} from './compare.controller';
// @ts-ignore
import {protectCustomerNumber, protectCustomerEmail} from '@techsee/techsee-common/lib/utils';
export type IHistoryConsoleResult = IHistoryConsoleSessionResult;

export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

const HISTORY_CONSOLE_MAIN_TAB_STORAGE_KEY = 'historyConsoleMainTabIndex';

/** Main nav tabs — values match `SimpleTabs` / `tabsList` order (0-based). */
export enum HistoryConsoleMainTab {
    Sessions = 0,
    Compare = 1
}

const HISTORY_CONSOLE_MAIN_TAB_ORDER: HistoryConsoleMainTab[] = [
    HistoryConsoleMainTab.Sessions,
    HistoryConsoleMainTab.Compare
];

function coerceHistoryConsoleMainTab(index: number): HistoryConsoleMainTab {
    return HISTORY_CONSOLE_MAIN_TAB_ORDER.includes(index as HistoryConsoleMainTab)
        ? (index as HistoryConsoleMainTab)
        : HistoryConsoleMainTab.Sessions;
}

export type IExpandedSessionScansValue = {timelineItems: ITimelineItem[]; results: any};

export class HistoryConsoleState {
    @observable customerRef: string = '';
    @observable customerPhone: string = '';
    @observable customerEmail: string = '';
    @observable roomId: string = '';
    @observable scanId: string = '';
    @observable preDataCollectionCode: string = '';
    @observable searchText: string = '';
    @observable startDate: number = 0;
    @observable endDate: number = 0;
    @observable isLoading: boolean = false;
    @observable sessionsResults: IHistoryConsoleResult[] = [];
    @observable totalResults: number = 0;
    @observable historyConsoleSessionsIndexSelected: number = 0;
    @observable sessionsTimeLineData: ITimelineItem[] = [];
    @observable searchError: string = '';
    expandedSessionScans: ObservableMap<string, IExpandedSessionScansValue> = observable.map<
        string,
        IExpandedSessionScansValue
    >();
    expandedSessionLoading: ObservableMap<string, boolean> = observable.map<string, boolean>();
    @observable pageIndex: number = 0;
    @observable selectedScanSessionId: string = '';
    @observable selectedScanIndex: number = -1;
    @observable sortField: string = '';
    @observable sortDesc: boolean = false;
    /** Persisted across refresh (sessionStorage); must stay in sync with `tabsList` order. */
    @observable mainTabIndex: HistoryConsoleMainTab = HistoryConsoleMainTab.Sessions;
}

export interface IHistoryConsoleController {
    state: HistoryConsoleState;
    compareController: ICompareController;
    translate: (token: string, data?: Record<string, any>) => string;
    dateFormat: string;
    readonly showStartLiveSession: boolean;
    applyUrlParams(params: IUrlParams): void;
    setSearchText(value: string): void;
    setDateRange(start: Date | null, end: Date | null): void;
    onSearch(): void;
    dispose(): void;
    onPageChange(pagination: DataTablePaginationState): void;
    onSetHistoryConsoleSessionsIndexSelected(index: number): void;
    fetchSessionScans(sessionId: string): void;
    onExpandedScanItemClick(sessionId: string, index: number): void;
    onHistoryConsoleTabChange(index: number): void;
    getScanDetails: ScanDetail | undefined;
    onSortingChange(sort: DataTableSortState | null): void;
    onSearchKeyDown(key: string): void;
    startLiveSession(): void;
}

export class HistoryConsoleController extends TechseeBaseController implements IHistoryConsoleController {
    readonly state: HistoryConsoleState;
    readonly compareController: ICompareController;
    readonly showStartLiveSession: boolean;
    private readonly _dateFormat: string;
    private readonly _historyConsoleService: IHistoryConsoleService;
    private readonly _wifiAnalyzerApiService: IWifiAnalyzerApiService;
    private readonly _appState: AppState;
    private _page: number = 0;
    private _perPage: number = DEFAULT_PAGE_SIZE;
    private readonly _accountData: any;
    private readonly _browserUtilsService: IBrowserUtilsService;
    private readonly _redirectionService: IRedirectionService;
    private readonly _localizationService: ILocalizationService;
    private _resultsReactionDisposer?: IReactionDisposer;
    private _sessionsIndexSelectedReactionDisposer?: IReactionDisposer;
    private readonly wifiScanService: WifiScanService;
    private readonly connectivityGuruThreshold?: ConnectivityGuruThreshold;
    private readonly _accountId: string;
    private readonly _userId: string;

    constructor(
        dateFormat: string,
        historyConsoleService: IHistoryConsoleService,
        appState: AppState,
        browserUtilsService: IBrowserUtilsService,
        accountData: any,
        localizationService: ILocalizationService,
        wifiAnalyzerApiService: IWifiAnalyzerApiService,
        userId: string,
        redirectionService: IRedirectionService
    ) {
        super();
        this.state = new HistoryConsoleState();
        this._dateFormat = dateFormat.split(' ')[0];
        this.showStartLiveSession = !!get(
            accountData,
            'protectedSettings.historyConsole.isStartLiveSessionEnabled',
            true
        );
        this._historyConsoleService = historyConsoleService;
        this._wifiAnalyzerApiService = wifiAnalyzerApiService;
        this._appState = appState;
        this._accountData = accountData;
        this._browserUtilsService = browserUtilsService;
        this._redirectionService = redirectionService;
        this._localizationService = localizationService;
        this._accountId = accountData?._id || '';
        this._userId = userId;
        this.state.sessionsResults = appState.historyConsoleSessionsResults;
        this.state.totalResults = appState.historyConsoleTotalResults;
        this.state.historyConsoleSessionsIndexSelected =
            +browserUtilsService.getFromSessionStorage('historyConsoleSessionsIndexSelected') || 0;
        const storedMainTab = +browserUtilsService.getFromSessionStorage(HISTORY_CONSOLE_MAIN_TAB_STORAGE_KEY);
        this.state.mainTabIndex = coerceHistoryConsoleMainTab(storedMainTab);
        this.onSetHistoryConsoleSessionsIndexSelected = this.onSetHistoryConsoleSessionsIndexSelected.bind(this);
        this.onHistoryConsoleTabChange = this.onHistoryConsoleTabChange.bind(this);
        this.connectivityGuruThreshold = get(this._accountData, 'protectedSettings.connectivityGuru') as
            | ConnectivityGuruThreshold
            | undefined;

        this.translate = (token: string, data?: Record<string, any>): string =>
            this._localizationService.translate(token, data);

        this.wifiScanService = new WifiScanService();

        this.compareController = new CompareController({
            historyConsoleState: this.state,
            wifiAnalyzerApiService: this._wifiAnalyzerApiService,
            accountId: this._accountId,
            connectivityGuruThreshold: this.connectivityGuruThreshold,
            wifiScanService: this.wifiScanService,
            translate: this.translate,
            getSearchParams: () => this.buildStateSearchParams(),
            fetchScanPreviews: (sessionId: string) => this.fetchScanPreviews(sessionId),
            mapScanToTimelineItem: (scan: any, index: number, selectedIndex: number) =>
                this.mapScanToTimelineItem(scan, index, selectedIndex)
        });

        this._resultsReactionDisposer = reaction(
            () => this.state.sessionsResults,
            async (results) => {
                this.setTimeLineData();

                if (this.state.sessionsResults[this.state.historyConsoleSessionsIndexSelected]) {
                    await this.onInitExpandedScan(
                        this.state.sessionsResults[this.state.historyConsoleSessionsIndexSelected].sessionId
                    );
                }

                await this.compareController.setDataForCompare();
            },
            {
                fireImmediately: true
            }
        );

        this._sessionsIndexSelectedReactionDisposer = reaction(
            () => this.state.historyConsoleSessionsIndexSelected,
            async (index) => {
                const session = this.state.sessionsResults[this.state.historyConsoleSessionsIndexSelected];

                this.updateTimelineSelection();

                if (session) {
                    await this.onInitExpandedScan(session.sessionId, 0);
                }
            },
            {fireImmediately: true}
        );
    }

    @action
    onSetHistoryConsoleSessionsIndexSelected(index: number) {
        runInAction(() => {
            this.state.historyConsoleSessionsIndexSelected = index;
        });
        this._browserUtilsService.saveToSessionStorage('historyConsoleSessionsIndexSelected', String(index));
        this.updateTimelineSelection();
    }

    private buildStateSearchParams(): Record<string, any> {
        const {state} = this;
        const params: Record<string, any> = {page: this._page, perPage: this._perPage};

        if (state.searchText) {
            params.searchText = state.searchText;
        }
        if (state.customerRef) {
            params.customerId = state.customerRef;
        }
        if (state.customerPhone) {
            params.customerPhone = state.customerPhone;
        }
        if (state.customerEmail) {
            params.customerEmail = state.customerEmail;
        }
        if (state.roomId) {
            params.sessionId = state.roomId;
        }
        if (state.preDataCollectionCode) {
            params.preDataCollectionCode = state.preDataCollectionCode;
        }
        if (state.startDate) {
            params.startDate = state.startDate;
        }
        if (state.endDate) {
            params.endDate = state.endDate;
        }

        return params;
    }

    private storeSessionScansIfNeeded(sessionId: string, scans: any[], selectedIdx: number): void {
        if (!this.state.expandedSessionScans.has(sessionId)) {
            this.state.expandedSessionScans.set(sessionId, {
                timelineItems: scans.map((s, i) => this.mapScanToTimelineItem(s, i, selectedIdx)),
                results: scans
            });
        }
    }

    private async fetchScanPreviews(sessionId: string): Promise<any[]> {
        const result = await this._wifiAnalyzerApiService.getBulk(this._accountData?._id, {roomId: sessionId} as any);

        return result?.previews || [];
    }

    private buildScanDetailInfo(scan: any): ScanDetailInfoItem[] {
        const ns = scan.networkSpeed;
        const wifi = scan.wifiConnectionInfo;
        const networkDiscoveryDevices = scan.networkDiscoveryDevices;
        const accessChannel = getAccessChannel(scan);
        const info: ScanDetailInfoItem[] = [];

        if (ns?.downloadSpeedMbps != null) {
            info.push({
                title: this.translate('REACT.HISTORY_CONSOLE.COMPARE_DOWNLOAD_SPEED'),
                value: `${ns.downloadSpeedMbps} Mbps`,
                symbol: Download
            });
        }
        if (ns?.upSpeedMbps != null) {
            info.push({
                title: this.translate('REACT.HISTORY_CONSOLE.COMPARE_UPLOAD_SPEED'),
                value: `${ns.upSpeedMbps} Mbps`,
                symbol: Upload
            });
        }
        if (wifi?.signalStrengthPercentage != null) {
            info.push({
                title: this.translate('REACT.HISTORY_CONSOLE.COMPARE_SIGNAL_STRENGTH'),
                value: `${wifi.signalStrengthPercentage}%`,
                symbol: SignalStrength
            });
        }
        if (accessChannel.length && accessChannel[0].channel >= 0) {
            info.push({
                title: this.translate('REACT.HISTORY_CONSOLE.COMPARE_CHANNEL_NUMBER'),
                value: `${this.translate('REACT.HISTORY_CONSOLE.COMPARE_CHANNEL_PREFIX')} ${accessChannel[0].totalAP}`,
                symbol: ChannelNumber
            });
        }
        if (networkDiscoveryDevices?.length) {
            info.push({
                title: this.translate('REACT.HISTORY_CONSOLE.COMPARE_CONNECTED_DEVICES'),
                value: String(networkDiscoveryDevices.length),
                symbol: ConnectedDevices
            });
        }

        return info;
    }

    private getSignalStrengthColor(
        value: number,
        spotLightLabels: SpotLightLabels,
        higherIsBetter: boolean,
        threshold: ThresholdConfig
    ): string {
        const level = this.wifiScanService.evalThreshold(spotLightLabels, value, threshold, higherIsBetter);

        return this.wifiScanService.getIconColor(level);
    }

    @action
    setTimeLineData() {
        this.state.sessionsTimeLineData = this.state.sessionsResults.map((item, index) => {
            const scanData = item.scanData;
            const ar = isArScan(scanData);

            return {
                type: getRootStore().localizationService.translate('REACT.INVITE.START_WITH_MODES.NATIVE_FULL_ASSIST'),
                time: this.formatDateTime(item.date),
                isSelected: index === this.state.historyConsoleSessionsIndexSelected,
                imageUrl: ar ? scanData?.ssidImageUrl : '',
                visualScanCount: scanData?.visualScanCount,
                wifiScanCount: scanData?.wifiScanCount,
                rssi: ar ? undefined : scanData?.rssi,
                signalStrengthPercentage: ar
                    ? undefined
                    : {
                          value: scanData?.signalStrengthPercentage,
                          color:
                              this.connectivityGuruThreshold?.signalStrength && scanData?.rssi != null
                                  ? this.getSignalStrengthColor(
                                        scanData.rssi,
                                        SpotLightLabels.signal,
                                        true,
                                        this.connectivityGuruThreshold?.signalStrength as ThresholdConfig
                                    )
                                  : undefined
                      }
            };
        });
    }

    @action
    updateTimelineSelection() {
        this.state.sessionsTimeLineData.forEach((item, index) => {
            item.isSelected = index === this.state.historyConsoleSessionsIndexSelected;
        });
    }

    private formatDateTime(dateString: string): string {
        return tsDate(dateString).format('DD MMM YYYY h:mmA');
    }

    get dateFormat(): string {
        return this._dateFormat;
    }

    @action
    setSearchText(value: string) {
        this.state.searchText = value;
    }

    @action
    setDateRange(start: Date | null, end: Date | null) {
        this.state.startDate = start ? start.getTime() : 0;
        this.state.endDate = end ? end.getTime() : 0;
    }

    @action
    onPageChange(page: DataTablePaginationState) {
        this._page = page.pageIndex;
        this._perPage = page.pageSize;
        this.state.pageIndex = page.pageIndex;
        this.onSearch();
    }

    @action
    onSortingChange(sort: DataTableSortState | null) {
        this.state.sortField = sort?.field ?? '';
        this.state.sortDesc = sort?.desc ?? false;
    }

    onSearchKeyDown(key: string) {
        if (key === 'Enter' && this.state.searchText) {
            this.onSearch();
        }
    }

    startLiveSession() {
        const params: IUrlParams = {};

        if (this.state.customerRef) {
            params.customerRef = this.state.customerRef;
        }
        if (this.state.customerPhone) {
            params.phoneNumber = this.state.customerPhone;
        }
        if (this.state.customerEmail) {
            params.customerEmail = this.state.customerEmail;
        }

        this._redirectionService.goToInvitePage(params);
    }

    async onSearch() {
        const {state} = this;

        runInAction(() => {
            state.isLoading = true;
            state.searchError = '';
            state.sessionsResults = [];
            state.selectedScanSessionId = '';
            state.selectedScanIndex = -1;
        });

        try {
            const params = this.buildStateSearchParams() as IHistoryConsoleSearchParams;

            if (state.scanId) {
                params.scanId = state.scanId;
            }

            const data = await this._historyConsoleService.getSessions(params);
            const results = data.historyConsoleDataSessions || [];

            runInAction(() => {
                this._appState.historyConsoleSessionsResults = results;
                this._appState.historyConsoleTotalResults = data.totalResults || 0;
                state.sessionsResults = results;
                state.totalResults = data.totalResults || 0;
                state.isLoading = false;
            });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Search failed';

            runInAction(() => {
                state.searchError = message;
                state.isLoading = false;
            });
        }
    }

    applyUrlParams(params: IUrlParams) {
        if (params.customerRef) {
            this.state.customerRef = params.customerRef;
        }
        if (params.customerPhone || params.phoneNumber) {
            this.state.customerPhone = (params.customerPhone || params.phoneNumber) as string;
        }
        if (params.customerEmail) {
            this.state.customerEmail = params.customerEmail;
        }
        if (params.roomId) {
            this.state.roomId = params.roomId;
        }
        if (params.scanId) {
            this.state.scanId = params.scanId;
        }
        if (params.preDataCollectionCode) {
            this.state.preDataCollectionCode = params.preDataCollectionCode;
        }
        if (params.startDate) {
            this.state.startDate = Number(params.startDate);
        }
        if (params.endDate) {
            this.state.endDate = Number(params.endDate);
        }

        const customerIdentifier =
            params.customerRef || params.customerEmail || params.customerPhone || params.phoneNumber;

        if (customerIdentifier) {
            this.state.searchText = customerIdentifier as string;
        } else if (params.roomId) {
            this.state.searchText = params.roomId;
        } else if (params.preDataCollectionCode) {
            this.state.searchText = params.preDataCollectionCode;
        }

        this.logStorylineLoading(params);
    }

    private logStorylineLoading(params: IUrlParams) {
        const urlParams: Record<string, string | number> = {};

        if (params.customerRef) {
            urlParams.customerRef = params.customerRef;
        }
        if (params.customerPhone || params.phoneNumber) {
            const phone = (params.customerPhone || params.phoneNumber) as string;

            urlParams.customerPhone = protectCustomerNumber(phone);
        }
        if (params.customerEmail) {
            urlParams.customerEmail = protectCustomerEmail(params.customerEmail);
        }
        if (params.roomId) {
            urlParams.roomId = params.roomId;
        }
        if (params.scanId) {
            urlParams.scanId = params.scanId;
        }
        if (params.preDataCollectionCode) {
            urlParams.preDataCollectionCode = params.preDataCollectionCode;
        }
        if (params.startDate) {
            urlParams.startDate = params.startDate;
        }
        if (params.endDate) {
            urlParams.endDate = params.endDate;
        }

        this.addEventLog({
            type: LOG_EVENTS.storylineLoading,
            accountId: this._accountId,
            userId: this._userId,
            level: EVENT_LEVELS.info,
            sentBy: EVENT_SOURCES.dashboard,
            meta: {params: urlParams}
        });
    }

    async onInitExpandedScan(sessionId: string, index: number = 0) {
        await this.fetchSessionScans(sessionId);

        this.onExpandedScanItemClick(sessionId, index);
    }

    async fetchSessionScans(sessionId: string) {
        if (this.state.expandedSessionScans.has(sessionId) || this.state.expandedSessionLoading.get(sessionId)) {
            return;
        }

        runInAction(() => {
            this.state.expandedSessionLoading.set(sessionId, true);
        });

        try {
            const previews = await this.fetchScanPreviews(sessionId);

            runInAction(() => {
                const isFirstSelection = this.state.selectedScanSessionId === '';
                const selectedIdx = isFirstSelection ? 0 : -1;

                this.state.expandedSessionScans.set(sessionId, {
                    timelineItems: previews.map((scan: any, index: number) =>
                        this.mapScanToTimelineItem(scan, index, selectedIdx)
                    ),
                    results: previews
                });

                if (isFirstSelection && previews.length > 0) {
                    this.state.selectedScanSessionId = sessionId;
                    this.state.selectedScanIndex = 0;
                }

                this.state.expandedSessionLoading.set(sessionId, false);
            });
        } catch (err: unknown) {
            runInAction(() => {
                this.state.expandedSessionScans.set(sessionId, {timelineItems: [], results: null});
                this.state.expandedSessionLoading.set(sessionId, false);
            });
        }
    }

    @action
    onExpandedScanItemClick(sessionId: string, index: number) {
        const prevSessionId = this.state.selectedScanSessionId;

        if (prevSessionId && prevSessionId !== sessionId) {
            const prevEntry = this.state.expandedSessionScans.get(prevSessionId);

            if (prevEntry) {
                this.state.expandedSessionScans.set(prevSessionId, {
                    timelineItems: prevEntry.timelineItems.map((item) => ({...item, isSelected: false})),
                    results: prevEntry.results
                });
            }

            const newIndex = this.state.sessionsResults.findIndex((s) => s.sessionId === sessionId);
            if (newIndex !== -1) {
                this.state.historyConsoleSessionsIndexSelected = newIndex;
                this._browserUtilsService.saveToSessionStorage('historyConsoleSessionsIndexSelected', String(newIndex));
            }
        }

        this.state.selectedScanSessionId = sessionId;
        this.state.selectedScanIndex = index;

        const entry = this.state.expandedSessionScans.get(sessionId);

        if (entry) {
            this.state.expandedSessionScans.set(sessionId, {
                timelineItems: entry.timelineItems.map((item, i) => ({...item, isSelected: i === index})),
                results: entry.results
            });
        }
    }

    private mapScanToTimelineItem(scan: any, index: number, selectedIndex: number): ITimelineItem {
        const ar = isArScan(scan);
        const rssi = scan.wifiConnectionInfo?.rssi;
        const signalStrength = scan.wifiConnectionInfo?.signalStrengthPercentage;

        return {
            type: getRootStore().localizationService.translate(
                ar ? 'REACT.HISTORY_CONSOLE.VISUAL_SCAN' : 'REACT.WIFI_ANALYZER.WIFI_SCAN'
            ),
            time: tsDate(scan.createdOn).format('DD MMM YYYY, h:mm A'),
            isSelected: index === selectedIndex,
            imageUrl: ar ? getScanImageUrl(scan.connectivityHeatmapData?.imageUrls) : '',
            visualScanCount: 0,
            wifiScanCount: 0,
            rssi: ar ? undefined : rssi,
            signalStrengthPercentage: ar
                ? undefined
                : signalStrength != null
                  ? {
                        value: signalStrength,
                        color:
                            this.connectivityGuruThreshold?.signalStrength && rssi != null
                                ? this.getSignalStrengthColor(
                                      rssi,
                                      SpotLightLabels.signal,
                                      true,
                                      this.connectivityGuruThreshold.signalStrength as ThresholdConfig
                                  )
                                : undefined
                    }
                  : undefined
        };
    }

    @action
    onHistoryConsoleTabChange(index: number) {
        const previousTab = this.state.mainTabIndex;
        const tabIndex = coerceHistoryConsoleMainTab(index);

        this.logStorylineTabInteraction(previousTab, tabIndex);

        this.state.mainTabIndex = tabIndex;
        this._browserUtilsService.saveToSessionStorage(HISTORY_CONSOLE_MAIN_TAB_STORAGE_KEY, String(tabIndex));
        if (tabIndex === HistoryConsoleMainTab.Compare) {
            this.compareController.onCompareTabSelected();
        }
    }

    private getTabName(tabIndex: HistoryConsoleMainTab): string {
        switch (tabIndex) {
            case HistoryConsoleMainTab.Sessions:
                return 'Sessions';
            case HistoryConsoleMainTab.Compare:
                return 'Compare';
            default:
                return `Tab_${tabIndex}`;
        }
    }

    private logStorylineTabInteraction(fromTab: HistoryConsoleMainTab, toTab: HistoryConsoleMainTab) {
        this.addEventLog({
            type: LOG_EVENTS.storylineTabInteraction,
            accountId: this._accountId,
            userId: this._userId,
            level: EVENT_LEVELS.info,
            sentBy: EVENT_SOURCES.dashboard,
            meta: {
                fromTab: this.getTabName(fromTab),
                toTab: this.getTabName(toTab)
            }
        });
    }

    @computed
    get getScanDetails(): ScanDetail | undefined {
        const entry = this.state.expandedSessionScans.get(this.state.selectedScanSessionId);
        const previews = entry?.results;
        const selectedPreview =
            this.state.selectedScanIndex >= 0 && previews?.[this.state.selectedScanIndex]
                ? previews[this.state.selectedScanIndex]
                : undefined;

        if (!selectedPreview) {
            return undefined;
        }

        const scanDetailsRaw = selectedPreview.scanDetails;

        return {
            customerId: selectedPreview.customerId ?? '',
            sessionId: selectedPreview.roomId ?? '',
            date: this.formatDateTime(selectedPreview.createdAt),
            insights: selectedPreview.insights?.wifiInsightsLive,
            imageUrl: getScanImageUrl(selectedPreview?.connectivityHeatmapData?.imageUrls) || '',
            details: scanDetailsRaw
                ? {
                      floor: scanDetailsRaw.floor,
                      room: scanDetailsRaw.roomType,
                      note: scanDetailsRaw.note
                  }
                : undefined,
            info: this.buildScanDetailInfo(selectedPreview)
        };
    }

    @action
    dispose() {
        if (this._resultsReactionDisposer) {
            this._resultsReactionDisposer();
            this._resultsReactionDisposer = undefined;
        }

        if (this._sessionsIndexSelectedReactionDisposer) {
            this._sessionsIndexSelectedReactionDisposer();
            this._sessionsIndexSelectedReactionDisposer = undefined;
        }

        this.compareController.dispose();

        runInAction(() => {
            this.state.expandedSessionScans.clear();
            this.state.expandedSessionLoading.clear();
            this.state.sessionsTimeLineData.splice(0);
            this.state.sessionsResults.splice(0);
        });

        super.dispose?.();
    }
}
